/**
 * Lectures de l'espace Curieux, partagées par l'Accueil et JeJoue (mêmes clés
 * React Query : aucune requête en double quand on passe de l'un à l'autre).
 */

import { useQuery } from '@tanstack/react-query';
import type { ExerciseDifficulty, SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

export interface PlayerExerciseRow {
  id: string;
  title: string;
  difficulty: ExerciseDifficulty;
  estimated_minutes: number | null;
  chapters: {
    id: string;
    title: string;
    slug: string;
    subject: SubjectSlug;
    level: SchoolLevel;
  } | null;
}

/** Exercices terminés par le Curieux (pour « déjà fait » et le compteur). */
export function usePlayerCompletedAttempts(playerId: string) {
  return useQuery({
    queryKey: ['player-completed-attempts', playerId],
    enabled: !!playerId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('attempts')
        .select('exercise_id, completed_at')
        .eq('student_id', playerId)
        .eq('status', 'completed');
      if (error) throw error;
      return (data ?? []) as {
        exercise_id: string | null;
        completed_at: string | null;
      }[];
    },
  });
}

/** Ensemble des exercices déjà terminés. */
export function completedExerciseIds(
  attempts: readonly { exercise_id: string | null }[] | undefined,
): Set<string> {
  return new Set((attempts ?? []).map((a) => a.exercise_id).filter((id): id is string => !!id));
}

/** Catalogue public (exercices publiés), filtré côté serveur par niveau / matière / difficulté. */
export function usePlayerExercises(filters: {
  subject?: SubjectSlug | '';
  level?: SchoolLevel | '';
  difficulty?: ExerciseDifficulty | '';
  limit?: number;
}) {
  const { subject = '', level = '', difficulty = '', limit = 200 } = filters;
  return useQuery({
    queryKey: ['player-exercises', subject, level, difficulty, limit],
    staleTime: 30_000,
    queryFn: async (): Promise<PlayerExerciseRow[]> => {
      let q = supabase
        .from('exercises')
        .select(
          `id, title, difficulty, estimated_minutes,
           chapters!inner(id, title, slug, subject, level)`,
        )
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (difficulty) q = q.eq('difficulty', difficulty);
      if (subject) q = q.eq('chapters.subject', subject);
      if (level) q = q.eq('chapters.level', level);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as PlayerExerciseRow[];
    },
  });
}

/** Paramètres de la route du player d'exercice. */
export function exerciseRouteParams(ex: PlayerExerciseRow) {
  if (!ex.chapters) return null;
  return {
    subject: ex.chapters.subject,
    level: ex.chapters.level,
    chapter: ex.chapters.slug,
    exercise: ex.id,
  };
}
